// slider/index.recipe.ts
import {defineSlotRecipe} from '@chakra-ui/react3'

// Shared base styles for horizontalCards and uspSummaryStep.
// uspSummaryStep always uses these styles (no responsive breakpoints),
// while horizontalCards overrides some properties at the `s` breakpoint.
const horizontalCardsBaseStyles = {
    sliderContainer: {
        '--items-per-slide': 1,
        '--slide-gap': 'spacing-4',
        '--slider-padding-inline': '{spacing.spacing-24}',
        display: 'flex',
        flexDirection: 'column-reverse',
        position: 'relative',
        width: '100%',
        overflow: 'visible'
    },
    scrollContainer: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        gap: 'spacing-8',
        overflowX: 'auto',
        alignItems: 'stretch'
    },
    controlsContainer: {
        position: 'static',
        display: 'flex',
        flexDirection: 'row-reverse',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: 'spacing-32',
        paddingInline: '0',
        width: '100%',
        gap: '0'
    },
    buttonGroup: {
        display: 'flex',
        gap: 'spacing-8'
    },
    slide: {
        display: 'flex',
        flex: '0 0 100%'
    }
}

export const sliderSlotRecipe = defineSlotRecipe({
    className: 'slider',
    slots: [
        'sliderContainer',
        'controlsContainer',
        'pagination',
        'paginationItem',
        'slide',
        'scrollContainer',
        'buttonGroup',
        'cockpitPagination'
    ],

    base: {
        sliderContainer: {
            display: 'flex',
            position: 'relative',
            flexDirection: 'column-reverse',
            overflow: 'visible'
        },
        scrollContainer: {
            overflowX: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': {display: 'none'},
            WebkitOverflowScrolling: 'touch',
            // for full visible focus indicator for (first) visible element
            paddingY: 'spacing-4',
            gap: 'var(--slide-gap, 0px)',
            borderRadius: {s: 'l'}
        },
        controlsContainer: {
            paddingTop: {base: 'spacing-40', s: 'spacing-80'},
            zIndex: 2
        },
        slide: {
            flex: '0 0 auto',
            height: 'auto',
            position: 'relative',
            overflow: 'visible',
            width: 'calc((100% - ( (var(--items-per-slide, 1) - 1) * var(--slide-gap, 0px) )) / var(--items-per-slide, 1))',
            img: {pointerEvents: 'none', userSelect: 'none', draggable: false}
        },
        pagination: {display: 'flex', alignItems: 'center', gap: 'spacing-4'},
        paginationItem: {
            height: '1px',
            width: 'spacing-16',
            backgroundColor: 'background.actions.carousel-bar',
            borderRadius: 'full',
            transition: 'all 0.3s ease',
            _current: {
                width: 'spacing-36',
                backgroundColor: 'border.brand'
            }
        },
        cockpitPagination: {display: 'none'},
        buttonGroup: {gap: 'spacing-8'}
    },

    variants: {
        variant: {
            carousel: {
                sliderContainer: {
                    '--items-per-slide': {base: 1, s: 3},
                    '--slide-gap': 'sizes.4'
                },
                controlsContainer: {justifyContent: 'flex-end'},
                scrollContainer: {
                    // using negative margin to span the full visible width
                    // --container-total-inline-padding is computed by parent module-container and includes:
                    // 1. container inline padding (responsive: mobile/desktop)
                    // 2. extra margin from max-width constraint: max(0px, 50cqw - maxWidth * 0.5)
                    // fallback to --container-inline-padding for usage outside module-container
                    marginInline:
                        'calc(-1 * var(--container-total-inline-padding, var(--container-inline-padding, 0px)))',
                    // offset children so they are aligned with the module content
                    paddingInline:
                        'var(--container-total-inline-padding, var(--container-inline-padding, 0px))',
                    scrollPaddingInline:
                        'var(--container-total-inline-padding, var(--container-inline-padding, 0px))'
                },
                pagination: {display: 'none'}
            },
            gallery: {
                slide: {
                    display: 'contents'
                },
                sliderContainer: {
                    '--items-per-slide': 1,
                    '--slide-gap': '0px'
                },
                scrollContainer: {
                    position: 'relative',
                    marginInline: {base: 'calc(-1 * var(--container-inline-padding))', s: 0},
                    display: 'grid',
                    gridAutoFlow: 'column',
                    gridTemplateRows: {base: 'repeat(2, auto)', s: 'auto'},
                    gridAutoColumns: '100%'
                },
                controlsContainer: {
                    flexDirection: {base: 'row-reverse', s: 'revert'},
                    justifyContent: 'space-between',
                    position: 'absolute',
                    left: 'spacing-32',
                    bottom: 'spacing-32',
                    right: {base: 'spacing-32', s: 'auto'},
                    gap: {base: 0, s: 'spacing-32'}
                },
                paginationItem: {
                    backgroundColor: 'background.actions.on-media.carousel-bar',
                    _current: {
                        backgroundColor: 'background.actions.on-media.active'
                    }
                }
            },
            cockpitGallery: {
                sliderContainer: {
                    '--items-per-slide': 1,
                    '--slide-gap': {base: 'sizes.4', s: '0px'},
                    '--cockpit-gallery-padding': 'spacing.container-padding-mobile'
                },
                slide: {
                    background: 'background.section.default',
                    borderRadius: 'm',
                    overflow: 'hidden',
                    width: '100%'
                },

                scrollContainer: {
                    overflowX: 'auto',
                    display: 'flex',
                    flexDirection: 'row',
                    scrollSnapType: 'x mandatory',
                    gap: 'var(--slide-gap)',
                    borderRadius: {s: 'm'},
                    marginInline: {base: 'calc(-1 * var(--cockpit-gallery-padding))', s: 0},
                    paddingInline: {
                        base: 'calc(var(--cockpit-gallery-padding) )',
                        s: 0
                    },
                    scrollPaddingInline: {base: 'var(--cockpit-gallery-padding)', s: 0},
                    paddingY: 'spacing-0'
                },
                controlsContainer: {
                    flexDirection: {base: 'row-reverse', s: 'revert'},
                    justifyContent: 'space-between',
                    position: 'absolute',
                    bottom: 'spacing-16',
                    right: {base: 'spacing-16', s: 'spacing-16'},
                    gap: {base: 0, s: 'spacing-32'},
                    paddingTop: 0
                },
                pagination: {display: 'none'},
                cockpitPagination: {
                    display: 'flex',
                    width: '2.875rem'
                },
                buttonGroup: {
                    alignItems: 'center',
                    p: 'spacing-6',
                    borderRadius: 'round',
                    bg: 'primary.base.white',
                    textStyle: 'heading.small',
                    height: '2.25rem'
                }
            },
            configuratorGallery: {
                sliderContainer: {
                    '--items-per-slide': 1,
                    position: 'absolute',
                    inset: '0'
                },
                scrollContainer: {
                    containerType: 'size',
                    position: 'absolute',
                    inset: '0',
                    paddingY: '0',
                    borderRadius: 'none'
                },
                slide: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '& > *': {
                        height: 'min(100cqw, 100cqh)',
                        width: 'min(100cqw, 100cqh)',
                        aspectRatio: '1'
                    }
                },
                controlsContainer: {
                    flexDirection: {base: 'row-reverse', s: 'revert'},
                    justifyContent: 'space-between',
                    position: 'absolute',
                    bottom: 'spacing-16',
                    right: {base: 'spacing-16', s: 'spacing-16'},
                    gap: {base: 0, s: 'spacing-32'}
                },
                pagination: {display: 'none'},
                cockpitPagination: {
                    display: 'flex',
                    justifyContent: 'center',
                    fontVariantNumeric: 'tabular-nums',
                    textStyle: 'heading.small',
                    whiteSpace: 'nowrap'
                },
                buttonGroup: {
                    alignItems: 'center',
                    p: 'spacing-6',
                    borderRadius: 'round',
                    bg: 'primary.base.white',
                    textStyle: 'heading.small'
                }
            },
            configuratorQuickview: {
                sliderContainer: {
                    width: '100%',
                    mx: 'auto'
                },
                scrollContainer: {
                    paddingY: '0',
                    borderRadius: 'none',
                    aspectRatio: '1/1'
                },
                slide: {
                    background: 'background.section.default',
                    overflow: 'hidden'
                },
                controlsContainer: {
                    flexDirection: {base: 'row-reverse', s: 'revert'},
                    justifyContent: 'space-between',
                    position: 'absolute',
                    zIndex: 'docked',
                    bottom: 'spacing-16',
                    right: {base: 'spacing-32', s: 'spacing-16'},
                    gap: {base: 0, s: 'spacing-32'}
                },
                pagination: {display: 'none'},
                cockpitPagination: {display: 'flex'},
                buttonGroup: {
                    alignItems: 'center',
                    p: 'spacing-6',
                    borderRadius: 'round',
                    bg: 'primary.base.white',
                    textStyle: 'heading.small'
                }
            },
            horizontalCards: {
                ...horizontalCardsBaseStyles,
                sliderContainer: {
                    ...horizontalCardsBaseStyles.sliderContainer,
                    '--items-per-slide': {base: 1, s: 6},
                    display: {base: 'flex', s: 'contents'},
                    padding: {base: 'spacing-24', s: '0'}
                },
                scrollContainer: {
                    ...horizontalCardsBaseStyles.scrollContainer,
                    display: {base: 'flex', s: 'contents'},
                    gap: {base: 'spacing-8', s: '0'},
                    overflowX: {base: 'auto', s: 'visible'},
                    paddingInline: {base: 'var(--slider-padding-inline)', s: '0'},
                    marginInline: {base: 'calc(-1 * var(--slider-padding-inline))', s: '0'},
                    scrollPaddingInline: {base: 'var(--slider-padding-inline)', s: '0'}
                },
                controlsContainer: {
                    ...horizontalCardsBaseStyles.controlsContainer,
                    display: {base: 'flex', s: 'none'}
                },
                slide: {
                    display: {base: 'flex', s: 'contents'},
                    flex: {base: '0 0 95%', s: 'unset'},
                    scrollSnapAlign: {base: 'center', s: 'start'}
                }
            },
            uspSummaryStep: {
                ...horizontalCardsBaseStyles,
                sliderContainer: {
                    ...horizontalCardsBaseStyles.sliderContainer,
                    '--items-per-slide': {base: 1, s: 1.8},
                    '--slide-gap': 'sizes.2',
                    paddingBlock: 'spacing-24'
                },
                scrollContainer: {
                    ...horizontalCardsBaseStyles.scrollContainer,
                    marginInlineStart: 'calc(-1 * var(--container-inline-padding-start, 0px))',
                    marginInlineEnd: 'calc(-1 * var(--container-inline-padding-end, 0px))',
                    paddingInlineStart: 'var(--container-inline-padding-start, 0px)',
                    paddingInlineEnd: 'var(--container-inline-padding-end, 0px)',
                    scrollPaddingInlineStart: 'var(--container-inline-padding-start, 0px)',
                    scrollPaddingInlineEnd: 'var(--container-inline-padding-end, 0px)',
                    overflowX: 'auto'
                },
                slide: {
                    ...horizontalCardsBaseStyles.slide,
                    flex: '0 0 auto',
                    scrollSnapAlign: 'start'
                }
            },
            productCarousel: {
                sliderContainer: {
                    '--items-per-slide': {base: 1.7, s: 4},
                    '--slide-gap': {base: 'sizes.2', s: 'sizes.4'}
                },
                scrollContainer: {
                    // span the viewport on mobile and increase the container width on desktop for the focus indicator to be fully visible
                    marginInline: {
                        base: 'calc(-1 * var(--container-total-inline-padding, var(--container-inline-padding, 0px)))',
                        s: '-spacing-4'
                    },
                    paddingInline: {
                        base: 'var(--container-total-inline-padding, var(--container-inline-padding, 0px))',
                        s: 'spacing-4'
                    },
                    scrollPaddingInline: {
                        base: 'var(--container-total-inline-padding, var(--container-inline-padding, 0px))',
                        s: 'spacing-4'
                    }
                },
                controlsContainer: {
                    flexDirection: 'row-reverse',
                    justifyContent: 'space-between'
                },
                cockpitPagination: {display: 'none'},
                buttonGroup: {
                    alignItems: 'center',
                    p: 'spacing-6',
                    borderRadius: 'round',
                    textStyle: 'heading.small'
                },
                pagination: {display: {base: 'flex', s: 'none'}}
            },
            promotionBanner: {
                sliderContainer: {
                    '--items-per-slide': 1,
                    '--slide-gap': 'spacing-8',
                    display: 'flex',
                    flexDirection: 'column-reverse',
                    width: '100%'
                },
                scrollContainer: {
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 'spacing-8',
                    paddingY: '0',
                    // Override base scrollContainer borderRadius: {s: 'l'} which clips
                    // promotion content at rounded edges due to overflow-x: auto
                    borderRadius: 'none'
                },
                controlsContainer: {
                    display: 'flex',
                    flexDirection: 'row-reverse',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: '0'
                },
                buttonGroup: {
                    display: 'flex',
                    gap: 'spacing-4'
                },
                slide: {
                    display: 'flex',
                    flex: '0 0 100%'
                }
            }
        },
        isScrollable: {
            false: {
                controlsContainer: {
                    display: 'none'
                },
                pagination: {
                    display: 'none'
                }
            }
        }
    },
    defaultVariants: {
        variant: 'default',
        hasMultiple: true
    }
})
